import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function BackofficeLogin() {
  const { login } = useAuth();
  const [mode, setMode] = useState("login"); // login | otp | forgot | reset
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // captcha
  const [captcha, setCaptcha] = useState(null); // {token, question}
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  // reset flow
  const [resetCode, setResetCode] = useState("");
  const [newPass, setNewPass] = useState("");

  const loadCaptcha = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/captcha");
      setCaptcha(data);
      setCaptchaAnswer("");
    } catch {
      setCaptcha(null);
    }
  }, []);

  useEffect(() => { loadCaptcha(); }, [loadCaptcha]);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/admin/login", {
        email, password: pass,
        captcha_token: captcha?.token || "",
        captcha_answer: captchaAnswer,
      });
      if (data.otp_required) {
        setMode("otp");
        toast.success("Verification code sent to your email");
      } else {
        login(data.token, data.user);
        toast.success("Welcome, Admin");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      loadCaptcha();
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/admin/verify", { email, code });
      login(data.token, data.user);
      toast.success("Welcome, Admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendResetCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/admin/forgot-password", { email });
      toast.success("If that admin email exists, a reset code has been sent.");
      setMode("reset");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (newPass.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/admin/reset-password", { email, code: resetCode, new_password: newPass });
      toast.success("Password reset. Please sign in.");
      setMode("login");
      setPass(""); setResetCode(""); setNewPass("");
      loadCaptcha();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1.5 bg-slate-900 border-slate-700 text-white";

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-6">
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                  className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-800 p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.png?v=3" alt="Health Coach Marathon" className="h-12 w-12 rounded-xl object-contain bg-white p-1" />
          <div>
            <div className="font-display text-xl font-extrabold text-white">Backoffice</div>
            <div className="text-xs text-slate-400">Health Coach Marathon — Admin</div>
          </div>
        </div>

        {mode === "login" && (
          <form onSubmit={doLogin} className="space-y-4">
            <div>
              <Label htmlFor="bo-email" className="text-slate-200">Email</Label>
              <Input id="bo-email" data-testid="admin-email-input" type="email" className={inputCls}
                     value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="bo-pass" className="text-slate-200">Password</Label>
              <Input id="bo-pass" data-testid="admin-password-input" type="password" className={inputCls}
                     value={pass} onChange={(e) => setPass(e.target.value)} required />
            </div>
            <div>
              <Label className="text-slate-200">Security check</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex items-center gap-1.5 rounded-lg bg-slate-900 border border-slate-700 px-3 h-10 font-mono-num text-white shrink-0" data-testid="admin-captcha-question">
                  {captcha ? `${captcha.question} =` : "…"}
                </div>
                <Input data-testid="admin-captcha-input" inputMode="numeric" className="bg-slate-900 border-slate-700 text-white font-mono-num"
                       placeholder="?" value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value.replace(/[^\d-]/g, ""))} required />
                <Button type="button" variant="ghost" size="icon" onClick={loadCaptcha} data-testid="admin-captcha-refresh"
                        className="text-slate-400 hover:text-white shrink-0" title="New question">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Button type="submit" disabled={busy || !captchaAnswer} data-testid="admin-login-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
            <button type="button" className="text-sm text-slate-400 hover:text-white w-full text-center"
                    data-testid="admin-forgot-link"
                    onClick={() => { setMode("forgot"); }}>
              Forgot password?
            </button>
          </form>
        )}

        {mode === "otp" && (
          <form onSubmit={doVerify} className="space-y-4">
            <p className="text-sm text-slate-400">Enter the 6-digit code sent to {email}.</p>
            <div>
              <Label htmlFor="bo-code" className="text-slate-200">6-digit code</Label>
              <Input id="bo-code" data-testid="admin-otp-input"
                     className="mt-1.5 tracking-[0.5em] font-mono-num text-lg bg-slate-900 border-slate-700 text-white"
                     inputMode="numeric" maxLength={6} value={code}
                     onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required />
            </div>
            <Button type="submit" disabled={busy || code.length !== 6} data-testid="admin-verify-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & continue"}
            </Button>
            <button type="button" className="text-sm text-slate-400 hover:text-white w-full flex items-center justify-center gap-1"
                    onClick={() => { setMode("login"); setCode(""); loadCaptcha(); }}>
              <ArrowLeft className="h-3 w-3" /> Back
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={sendResetCode} className="space-y-4">
            <p className="text-sm text-slate-400">Enter your admin email. We'll send a password reset code.</p>
            <div>
              <Label htmlFor="fp-email" className="text-slate-200">Email</Label>
              <Input id="fp-email" data-testid="admin-forgot-email-input" type="email" className={inputCls}
                     value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <Button type="submit" disabled={busy || !email} data-testid="admin-send-reset-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset code"}
            </Button>
            <button type="button" className="text-sm text-slate-400 hover:text-white w-full flex items-center justify-center gap-1"
                    onClick={() => setMode("login")}>
              <ArrowLeft className="h-3 w-3" /> Back to sign in
            </button>
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={doReset} className="space-y-4">
            <p className="text-sm text-slate-400">Enter the code sent to {email} and choose a new password.</p>
            <div>
              <Label htmlFor="rp-code" className="text-slate-200">Reset code</Label>
              <Input id="rp-code" data-testid="admin-reset-code-input"
                     className="mt-1.5 tracking-[0.4em] font-mono-num text-lg bg-slate-900 border-slate-700 text-white"
                     inputMode="numeric" maxLength={6} value={resetCode}
                     onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ""))} required />
            </div>
            <div>
              <Label htmlFor="rp-new" className="text-slate-200">New password</Label>
              <Input id="rp-new" data-testid="admin-reset-newpass-input" type="password" className={inputCls}
                     value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="At least 8 characters" required />
            </div>
            <Button type="submit" disabled={busy || resetCode.length !== 6 || newPass.length < 8} data-testid="admin-reset-submit-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
            </Button>
            <button type="button" className="text-sm text-slate-400 hover:text-white w-full flex items-center justify-center gap-1"
                    onClick={() => { setMode("login"); loadCaptcha(); }}>
              <ArrowLeft className="h-3 w-3" /> Back to sign in
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
